import { ServerError } from '@apollo/client/errors'
import { TargetModel, useAuthLogin, useCreateUser, useGeoLookup, useUpdateIntermediateProfileMutation } from '@lopo-tech/lopo-api'
import React, { useCallback, useState } from 'react'
import { Button } from 'src/components/button/button'
import { Checkbox } from 'src/components/forms/checkbox/checkbox'
import InputField from 'src/components/forms/input_field/input_field'
import { SvgIcon } from 'src/components/icons/svg-icon'
import PlaceSearch from 'src/components/location_picker/place_search'
import NavigationItem from 'src/components/navigation_item/navigation_item'
import { ProgressIndicator } from 'src/components/progress_indicator/progress_indicator'
import { showSnackBar } from 'src/components/snack_bar/snack_bar'
import { CLASS_PREFIX } from 'src/constants'
import { HTTP_TOO_MANY_REQUESTS } from 'src/constants/api_error_codes'
import { FRONTEND_DATA_AGB_PATH, FRONTEND_DATA_PROTECTION_PATH } from 'src/constants/urls'
import { IAddress } from 'src/interfaces/address'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'
import { IPlaceSuggestion } from 'src/utils/places/autocomplete'
import { textResources } from 'src/lang/de'
import { getErrorDetailsGQL, getFirstFullMessageGQL, getSchemaCoercionFieldGQL } from 'src/utils/api_error_message/api_error_message'
import { reportError } from 'src/utils/reporting/report-errors'
import { UrlUtils } from 'src/utils/url/url'
import { isNotEmpty, validateEmail, validatePassword } from 'src/utils/validation'
import { AUTH_CLS } from '../../auth/constants'
import './registration_form.scss'

type IProps = {
  onBack?: () => void
  onNext: (email: string) => void
  emailRedirectUrl?: string
  showHeader?: boolean
  footer?: React.ReactNode
  /**
   * Offered only when the account was created but the sign-in failed. The host
   * decides what reaching the login form means for it — the two embeddings differ:
   * one swaps a tab, the other changes its view — and both get the address the
   * user just typed so they can prefill it.
   */
  onGoToLogin?: (email: string) => void
}

const cls = `${CLASS_PREFIX}registration-form`

enum AddressError {
  NotReleased = 'area_not_released',
  Other = 'other'
}

/**
 * Registration is three chained requests, and they fail into three different worlds:
 * before the first one nothing exists, after it the account does, and after the second
 * one this component is no longer on screen — the page swaps it for the email
 * verification view as soon as the token lands. One catch cannot tell those apart, so
 * the step is tracked and each one is answered on its own terms.
 */
enum RegistrationStep {
  CreateUser,
  Login,
  UpdateProfile
}

export const RegistrationForm: React.FC<IProps> = ({ onBack, onNext, emailRedirectUrl, showHeader = true, footer, onGoToLogin }) => {
  const locationShape = useGetAppLocationShape()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [address, setAddress] = useState<IAddress | undefined>(undefined)

  const [validFirstName, setValidFirstName] = useState(true)
  const [validLastName, setValidLastName] = useState(true)
  const [validEmail, setValidEmail] = useState(true)
  const [validPassword, setValidPassword] = useState(true)
  const [validAddress, setValidAddress] = useState(true)
  const [addressError, setAddressError] = useState<AddressError | null>(null)
  const [validTerms, setValidTerms] = useState(true)

  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [loginFailed, setLoginFailed] = useState(false)
  const [emailErrorText, setEmailErrorText] = useState<string | undefined>(undefined)
  const [passwordErrorText, setPasswordErrorText] = useState<string | undefined>(undefined)

  const [addressQuery, setAddressQuery] = useState('')

  const addressErrorText = addressError === AddressError.NotReleased ? textResources.locationChanger.areaNotReleasedShort : textResources.locationChanger.addressNotFound

  const [createUser] = useCreateUser()
  const { login } = useAuthLogin()
  const [updateIntermediateProfile] = useUpdateIntermediateProfileMutation()
  const [runGeoLookup] = useGeoLookup()

  const handleSelectLocation = useCallback((suggestion: IPlaceSuggestion) => {
    setAddressQuery(suggestion.text)
    setAddressError(null)
    setValidAddress(true)
    setErrorMessage('')

    runGeoLookup({
      variables: {
        googlePlaceId: suggestion.placeId,
        streetRequired: true
      }
    }).then(({ data }) => {
      if (data?.geoLookup && data.geoLookup.length > 0) {
        if (!data.geoLookup[0].locationShape.released) {
          setAddressError(AddressError.NotReleased)
        }
        setAddress(data.geoLookup[0] as IAddress)
      } else {
        setAddressError(AddressError.Other)
      }
    })
  }, [runGeoLookup])

  const handleAddressQueryChange = useCallback((query: string) => {
    setAddressQuery(query)
    setErrorMessage('')
    if (address) {
      setAddress(undefined)
    }
  }, [address])

  const handleFailure = useCallback((step: RegistrationStep, error: unknown) => {
    // Which failure this is decides whether the way out is offered, and the step says it
    // exactly: `step` only reaches Login inside the `.then()` of `createUser`, which runs
    // only when that promise resolved. So this is true precisely when the account exists
    // and the sign-in was refused — the same set the message describes, established
    // without reading the message back.
    setLoginFailed(step === RegistrationStep.Login)

    if (step === RegistrationStep.UpdateProfile) {
      // Most hosts have already swapped this component for the verification view by now —
      // a successful login is enough to do that — so an inline message would render into
      // nothing. The snack bar lives at the app root and outlives it. It names the loss
      // rather than staying generic: the next screen announces the confirmation mail and
      // so reads as success, while the name never arrived.
      showSnackBar(textResources.authentication.errorNameNotSaved, { showClose: true })
      return
    }

    if (step === RegistrationStep.Login) {
      // Whichever way the login failed, the account is already there, and that is the
      // part the user needs: a second attempt would only report the email as taken.
      setErrorMessage(textResources.authentication.errorAccountCreatedLoginFailed)
      return
    }

    const fullMessage = getFirstFullMessageGQL(error)

    if (fullMessage) {
      // The backend writes finished sentences, and details names the fields they belong
      // to. Put the message next to its field, because one under the button leaves the
      // user hunting — but only when exactly one field is named: fullMessages[0] cannot
      // be attributed to a particular field once there are several.
      const fields = Object.keys(getErrorDetailsGQL(error) ?? {})
      const single = fields.length === 1 ? fields[0] : undefined

      if (single === 'email') {
        setValidEmail(false)
        setEmailErrorText(fullMessage)
      } else if (single === 'password') {
        setValidPassword(false)
        setPasswordErrorText(fullMessage)
      } else {
        setErrorMessage(fullMessage)
      }
      return
    }

    if (getSchemaCoercionFieldGQL(error) === 'email') {
      // A value the schema rejected before the resolver ran, so there is no sentence to
      // show and the form supplies its own. The field comes out of the error rather than
      // being assumed: the input carries two custom scalars, and only the email one is
      // filled by the user — a rejected redirectUrl is our bug, not theirs, and belongs
      // in the generic branch below.
      setValidEmail(false)
      setEmailErrorText(textResources.shared.errorEmail)
      return
    }

    if (ServerError.is(error) && error.response.status === HTTP_TOO_MANY_REQUESTS) {
      setErrorMessage(textResources.shared.errorTooManyAttempts)
      return
    }

    if (!ServerError.is(error) && !navigator.onLine) {
      setErrorMessage(textResources.authentication.internetConnectionProblemBody)
      return
    }

    setErrorMessage(textResources.shared.errorUnknown)
  }, [])

  const handleSubmit = useCallback(() => {
    const emailValid = validateEmail(email)
    const passwordValid = validatePassword(password)
    const firstNameValid = isNotEmpty(firstName)
    const lastNameValid = isNotEmpty(lastName)
    const addressValid = !addressError
    const termsValid = termsAccepted

    setValidEmail(emailValid)
    setValidPassword(passwordValid)
    setValidFirstName(firstNameValid)
    setValidLastName(lastNameValid)
    setValidAddress(addressValid)
    setValidTerms(termsValid)

    if (!emailValid || !passwordValid || !firstNameValid || !lastNameValid || !addressValid || !termsValid) {
      return
    }

    const locationShapeId = address!.locationShape.id

    setErrorMessage('')
    setEmailErrorText(undefined)
    setPasswordErrorText(undefined)
    setIsLoading(true)

    let step = RegistrationStep.CreateUser

    createUser({
      variables: {
        input: {
          email,
          password,
          locationShapeId,
          ageCheckAccepted: true,
          privacyConditionsAccepted: true,
          termsOfUseAccepted: true,
          redirectUrl: emailRedirectUrl ?? UrlUtils.emailConfirmationRedirectUrl()
        }
      }
    })
      .then(() => {
        step = RegistrationStep.Login
        return login(email, password)
      })
      .then(() => {
        step = RegistrationStep.UpdateProfile
        return updateIntermediateProfile({
          variables: {
            input: {
              targetModel: TargetModel.PrivateProfile,
              firstName,
              lastName,
              placeId: address?.googlePlaceId ?? undefined
            }
          }
        })
      })
      .then((result) => {
        // This mutation reports a rejected value in its payload instead of rejecting the
        // promise, so without this check a failed profile update would pass for success.
        const payloadErrors = result?.data?.updateIntermediateProfile?.errors

        if (payloadErrors?.length) {
          // The user gets a fixed sentence rather than this payload, whose message is a
          // backend placeholder. The reason still has to survive: no link in the composed
          // Apollo chain inspects `data`, so nothing else would ever see it, and after the
          // fact the concrete cause would not be recoverable anywhere.
          reportError(new Error('Profile update rejected the submitted values'), { errors: payloadErrors })
          handleFailure(RegistrationStep.UpdateProfile, undefined)
        }

        onNext(email)
      })
      .catch((error) => {
        setIsLoading(false)
        handleFailure(step, error)

        // Only the last step moves on despite failing: the account exists and the user is
        // signed in, so holding them in front of a registration form would strand them —
        // a second attempt there only reports the email as taken. Navigation lives here
        // rather than in handleFailure, which picks the message and nothing else.
        if (step === RegistrationStep.UpdateProfile) {
          onNext(email)
        }
      })
  }, [email, password, firstName, lastName, address, termsAccepted, createUser, login, updateIntermediateProfile, onNext, handleFailure])

  return (
    <div className={cls}>
      {showHeader && (
        <div className={`${cls}__header`}>
          <button onClick={onBack}>
            <SvgIcon name="Arrows/chevron-left" /> {textResources.shared.back}
          </button>
          <ProgressIndicator steps={3} current={0} />
        </div>
      )}
      <div className={`${cls}__scroll`}>
        <div className={`${cls}__form`}>
          <div>
            <div className={`${AUTH_CLS}__header`}>
              {textResources.authentication.createAccountTitle}
            </div>
            <div className={`${AUTH_CLS}__subheader`}>
              {textResources.authentication.createAccountSubtitle}
            </div>
          </div>
          <div className={`${cls}__form__inputs`}>
            <div className={`${cls}__form__inputs__names`}>
              <InputField
                floatLabel={false}
                label={textResources.authentication.firstName}
                name="firstname"
                placeholder={textResources.authentication.enterName}
                value={firstName}
                onChange={(value) => {
                  setFirstName(value)
                  setValidFirstName(true)
                  setErrorMessage('')
                }}
                valid={validFirstName}
                errorText={
                  !validFirstName
                    ? textResources.authentication.firstNameRequired
                    : undefined
                }
              />
              <InputField
                floatLabel={false}
                label={textResources.authentication.lastName}
                name="lastname"
                placeholder={textResources.authentication.enterName}
                value={lastName}
                onChange={(value) => {
                  setLastName(value)
                  setValidLastName(true)
                  setErrorMessage('')
                }}
                valid={validLastName}
                errorText={
                  !validLastName
                    ? textResources.authentication.lastNameRequired
                    : undefined
                }
              />
            </div>
            <PlaceSearch
              autoFocus={false}
              type='address'
              area={locationShape?.slug}
              query={addressQuery}
              onChangeQuery={handleAddressQueryChange}
              onSelect={handleSelectLocation}
              onFocus={() => undefined}
              onHasResults={() => undefined}
              valid={validAddress}
              errorText={!validAddress ? addressErrorText : undefined}
              floatLabel={false}
              label={textResources.authentication.address}
              placeholder={textResources.authentication.enterAddress}
              noBackground={false}
              overlayResults
            />
            <InputField
              floatLabel={false}
              label={textResources.shared.email}
              name="email"
              placeholder={textResources.authentication.enterEmail}
              value={email}
              onChange={(value) => {
                setEmail(value)
                setValidEmail(true)
                setEmailErrorText(undefined)
                setErrorMessage('')
              }}
              valid={validEmail}
              errorText={
                !validEmail
                  ? emailErrorText ?? textResources.shared.errorEmail
                  : undefined
              }
            />
            <InputField
              floatLabel={false}
              label={textResources.shared.password}
              name="password"
              placeholder={textResources.authentication.createPassword}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(value) => {
                setPassword(value)
                setValidPassword(true)
                setPasswordErrorText(undefined)
                setErrorMessage('')
              }}
              valid={validPassword}
              errorText={
                !validPassword
                  ? passwordErrorText ?? textResources.authentication.errorPassword
                  : undefined
              }
            />
          </div>
          <Checkbox
            checked={termsAccepted}
            onClick={() => {
              setTermsAccepted(!termsAccepted)
              setValidTerms(true)
              setErrorMessage('')
            }}
            required
            valid={validTerms}
            label={
              <span>
                {textResources.authentication.ageConfirmation1}
                <NavigationItem
                  url={FRONTEND_DATA_AGB_PATH}
                  className={`${cls}__terms__link`}
                  target={'_blank'}
                >
                  {textResources.authentication.ageConfirmation2}
                </NavigationItem>
                {textResources.authentication.ageConfirmation3}
                <NavigationItem
                  url={FRONTEND_DATA_PROTECTION_PATH}
                  className={`${cls}__terms__link`}
                  target={'_blank'}
                >
                  {textResources.authentication.ageConfirmation4}
                </NavigationItem>
                {textResources.authentication.ageConfirmation5}
              </span>
            }
          />
        </div>
      </div>
      <div className={`${cls}__footer`}>
        {errorMessage && <div className={`${cls}__error`} role="alert">{errorMessage}</div>}
        {/*
          A sibling of the alert rather than a child of it, so the live region announces the
          sentence and not the button label with it. `errorMessage` carries the clearing —
          every path that empties the message hides the button with it, so the flag needs no
          reset of its own — and `loginFailed` carries the identity, so no rendered text is
          compared to decide what to show.
        */}
        {onGoToLogin && errorMessage && loginFailed && (
          <button
            type="button"
            className={`${cls}__error-action`}
            onClick={() => onGoToLogin(email)}
          >
            {textResources.shared.login}
          </button>
        )}
        <Button
          size="large"
          className={`${cls}__submit`}
          title={textResources.authentication.next}
          disabled={isLoading || !termsAccepted}
          onClick={handleSubmit}
        />
        {footer}
      </div>
    </div>
  )
}
